import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Container,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { BASE_END_POINT_COMPANY, BASE_END_POINT_JOB, BASE_END_POINT_FILE } from '../../config';
import RichEditorReadOnly from '../../components/RichEditorReadOnly/RichEditorReadOnly';
import usePageTitle from '../../hooks/usePageTitle';
import useStructuredData from '../../hooks/useStructuredData';
import useMetaTags from '../../hooks/useMetaTags';
import useCanonicalUrl from '../../hooks/useCanonicalUrl';
import { generateOrganizationSchema } from '../../helpers/structuredDataHelpers';
import { buildJobUrl, buildCompanyUrl } from '../../helpers/urlHelpers';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    backgroundColor: theme.palette.background.default
  },
  backButton: {
    textTransform: 'none',
    color: theme.palette.text.primary,
    fontWeight: 500,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(4),
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  heroSection: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(6),
    marginBottom: theme.spacing(4),
    border: `1px solid ${theme.palette.divider}`,
    textAlign: 'center'
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3)
  },
  heroCompanyLogo: {
    width: 120,
    height: 120,
    border: `2px solid ${theme.palette.divider}`
  },
  companyName: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  companyMeta: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3)
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  websiteButton: {
    textTransform: 'none',
    fontWeight: 600
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary
  },
  aboutSection: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    border: `1px solid ${theme.palette.divider}`
  },
  jobsSection: {
    marginTop: theme.spacing(6),
    padding: theme.spacing(6, 2),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    marginLeft: -theme.spacing(3),
    marginRight: -theme.spacing(3),
    marginBottom: -theme.spacing(8)
  },
  jobsSectionHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(4)
  },
  jobsSectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  jobsSectionSubtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 600,
    margin: '0 auto'
  },
  jobCard: {
    height: '100%',
    transition: 'all 0.3s ease',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
      borderColor: theme.palette.primary.main
    }
  },
  cardContent: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    objectFit: 'contain',
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  jobTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.4,
    minHeight: '2.8em'
  },
  jobCompanyName: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  icon: {
    fontSize: '1rem'
  },
  categoryChip: {
    marginTop: 'auto',
    alignSelf: 'flex-start'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(8)
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  }
}));

const CompanyPublicView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageTitle(`${company?.name || 'Company'} - Remote Company Profile`);

  // Generate Organization structured data for SEO
  const organizationSchema = useMemo(() => {
    if (!company) return null;

    return generateOrganizationSchema({
      name: company.name,
      description: company.detail || company.about,
      website: company.siteURL,
      url: company.siteURL,
      logo: logoUrl,
      logoUrl: logoUrl
    });
  }, [company, logoUrl]);

  // Inject structured data into page
  useStructuredData(organizationSchema, 'company-organization-schema');

  // Helper function to truncate text to max length
  const truncateText = (text, maxLength = 160) => {
    if (!text || typeof text !== 'string') return '';
    const plainText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  // Generate Open Graph and Twitter Card meta tags
  const metaTags = useMemo(() => {
    if (!company) return null;

    // Build SEO-friendly URL with slug
    const companyUrl = buildCompanyUrl(id, company.name);

    return {
      title: company.name,
      description: truncateText(company.detail || company.about || `${company.name} - Remote Company Profile`, 160),
      image: logoUrl || 'https://remote-job.ai/images/social-preview.jpg',
      url: `https://remote-job.ai${companyUrl}`,
      type: 'profile'
    };
  }, [company, logoUrl, id]);

  // Apply meta tags
  useMetaTags(metaTags);

  // Set canonical URL with slug
  const canonicalUrl = useMemo(() => {
    if (!company) return null;
    const companyUrl = buildCompanyUrl(id, company.name);
    return `https://remote-job.ai${companyUrl}`;
  }, [company, id]);

  useCanonicalUrl(canonicalUrl);

  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const fetchCompanyData = async () => {
      try {
        if (isMounted) setLoading(true);

        // Fetch company details
        console.log('🔍 [CompanyPublicView] Fetching company:', id);
        const companyResponse = await fetch(
          `${BASE_END_POINT_COMPANY}/api/v1/company/${id}`
        );

        if (!companyResponse.ok) {
          throw new Error('Company not found');
        }

        const companyResult = await companyResponse.json();
        const companyData = companyResult.data;
        console.log('✅ [CompanyPublicView] Company data:', companyData);
        if (isMounted) setCompany(companyData);

        // Fetch logo using userId (one logo per user/company)
        if (companyData?.userId && isMounted) {
          console.log('🖼️ [CompanyPublicView] Fetching logo for userId:', companyData.userId);
          try {
            const logoResponse = await fetch(
              `${BASE_END_POINT_FILE}/api/v1/file/user/${companyData.userId}`
            );
            console.log('🖼️ [CompanyPublicView] Logo response status:', logoResponse.status);
            if (logoResponse.ok) {
              const logoResult = await logoResponse.json();
              console.log('🖼️ [CompanyPublicView] Logo result:', logoResult);
              // API returns an array in data field
              if (logoResult.data && Array.isArray(logoResult.data) && logoResult.data.length > 0) {
                const logoFile = logoResult.data[0];
                if (logoFile.publicUrl && isMounted) {
                  console.log('✅ [CompanyPublicView] Logo URL:', logoFile.publicUrl);
                  setLogoUrl(logoFile.publicUrl);
                }
              }
            }
          } catch (logoError) {
            console.warn('⚠️ [CompanyPublicView] Could not fetch logo:', logoError);
          }
        }

        // Fetch company jobs - using user ID to find jobs
        if (companyData?.userId && isMounted) {
          console.log('🔍 [CompanyPublicView] Fetching jobs for userId:', companyData.userId);
          const jobsResponse = await fetch(
            `${BASE_END_POINT_JOB}/api/v1/job/active?page=0&size=50`
          );

          if (jobsResponse.ok) {
            const jobsResult = await jobsResponse.json();
            // Filter jobs by this company
            const companyJobs = jobsResult.data?.filter(
              (jobResult) => jobResult.company?.id === id
            ) || [];
            console.log('✅ [CompanyPublicView] Found jobs:', companyJobs.length);
            if (isMounted) setJobs(companyJobs);
          }
        }
      } catch (err) {
        console.error('❌ [CompanyPublicView] Error fetching company:', err);
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (id) {
      fetchCompanyData();
    }

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleJobClick = (jobId, jobTitle, companyName) => {
    history.push(buildJobUrl(jobId, jobTitle, companyName));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleWebsiteClick = () => {
    if (company?.siteURL) {
      const url = company.siteURL.startsWith('http') 
        ? company.siteURL 
        : `https://${company.siteURL}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatJobType = (type) => {
    if (!type) return 'N/A';
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      dev: 'Development',
      design: 'Design',
      marketing: 'Marketing',
      sales: 'Sales',
      customer_support: 'Customer Support',
      finance: 'Finance',
      operations: 'Operations',
      hr: 'Human Resources',
      legal: 'Legal',
      other: 'Other'
    };
    return labels[cat] || cat;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="h6" color="textSecondary">
            Loading company profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !company) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <Box className={classes.errorContainer}>
          <Typography variant="h4" gutterBottom>
            Company Not Found
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            The company you're looking for doesn't exist or has been removed.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => history.goBack()}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      {/* Back Button */}
      <Button
        className={classes.backButton}
        startIcon={<ArrowBackIcon />}
        onClick={() => history.goBack()}>
        Back
      </Button>

      {/* Hero Section - Company Header */}
      <Box className={classes.heroSection}>
        <Box className={classes.logoContainer}>
          <Avatar
            src={logoUrl || ''}
            className={classes.heroCompanyLogo}>
            <span role="img" aria-label="building" style={{ fontSize: '3rem' }}>
              🏢
            </span>
          </Avatar>
        </Box>
        
        <Typography variant="h3" className={classes.companyName}>
          {company.name}
        </Typography>

        <Box className={classes.companyMeta}>
          {company.location && (
            <Box className={classes.metaItem}>
              <span role="img" aria-label="location">📍</span>
              <Typography>{company.location}</Typography>
            </Box>
          )}
          
          {jobs.length > 0 && (
            <Box className={classes.metaItem}>
              <span role="img" aria-label="jobs">💼</span>
              <Typography>{jobs.length} Active {jobs.length === 1 ? 'Job' : 'Jobs'}</Typography>
            </Box>
          )}
        </Box>

        {company.siteURL && (
          <Button
            variant="outlined"
            color="primary"
            className={classes.websiteButton}
            onClick={handleWebsiteClick}
            startIcon={<span role="img" aria-label="globe">🌐</span>}>
            Visit Website
          </Button>
        )}
      </Box>

      {/* About Section */}
      {company.detail && (
        <Box className={classes.aboutSection}>
          <Typography variant="h5" className={classes.sectionTitle}>
            About {company.name}
          </Typography>
          <RichEditorReadOnly content={company.detail} />
        </Box>
      )}

      {/* Jobs Section */}
      <Box className={classes.jobsSection}>
        <Box className={classes.jobsSectionHeader}>
          <Typography variant="h4" className={classes.jobsSectionTitle}>
            Open Positions {jobs.length > 0 && `(${jobs.length})`}
          </Typography>
          <Typography variant="body1" className={classes.jobsSectionSubtitle}>
            {jobs.length > 0 
              ? `Explore ${jobs.length} open ${jobs.length === 1 ? 'position' : 'positions'} at ${company.name}`
              : 'Check back later for new opportunities'}
          </Typography>
        </Box>

        {jobs.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              No open positions at the moment
            </Typography>
            <Typography variant="body2">
              Check back later for new opportunities!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} style={{ maxWidth: 1200, margin: '0 auto' }}>
            {jobs.map((jobResult) => {
              const job = jobResult.job;
              const jobCompany = jobResult.company || {};
              const file = jobResult.file || {};

              return (
                <Grid item xs={12} sm={6} md={4} key={job.id}>
                  <Card className={classes.jobCard}>
                    <CardActionArea
                      onClick={() => handleJobClick(job.id, job.title, jobCompany.name || company.name)}
                      style={{ height: '100%' }}>
                      <CardContent className={classes.cardContent}>
                        {file.publicUrl && (
                          <img
                            src={file.publicUrl}
                            alt={jobCompany.name || 'Company logo'}
                            className={classes.companyLogo}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}

                        <Typography className={classes.jobTitle}>
                          {job.title}
                        </Typography>

                        <Typography className={classes.jobCompanyName}>
                          {jobCompany.name || company.name}
                        </Typography>

                        <Box className={classes.detailsRow}>
                          <WorkOutlineIcon className={classes.icon} />
                          <Typography variant="body2">
                            {formatJobType(job.type)}
                          </Typography>
                        </Box>

                        <Box className={classes.detailsRow}>
                          <LocationOnIcon className={classes.icon} />
                          <Typography variant="body2">
                            {job.regionalRestrictions || '🌍 Worldwide'}
                          </Typography>
                        </Box>

                        <Chip
                          label={getCategoryLabel(job.category)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          className={classes.categoryChip}
                        />
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default CompanyPublicView;

